import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';
import { Switch } from '../ui/Switch';
import { 
  shareApi, 
  CreateShareRequest, 
  expireOptions, 
  dataLimitOptions, 
  generateRandomPassword,
  buildShareUrl,
} from '../../services/shareApi';
import { 
  Link as LinkIcon, 
  Copy, 
  RefreshCw, 
  Check, 
  Lock, 
  Eye,
  Calendar,
  Settings,
} from 'lucide-react';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workflowExecutionId: string;
  workflowName: string;
}

export function ShareDialog({ 
  isOpen, 
  onClose, 
  workflowExecutionId,
  workflowName,
}: ShareDialogProps) {
  const { t } = useTranslation();
  
  // 表单状态
  const [password, setPassword] = useState('');
  const [usePassword, setUsePassword] = useState(true);
  const [expireHours, setExpireHours] = useState(168); // 默认7天
  const [dataLimit, setDataLimit] = useState(10); // 默认10条
  const [maxViewCount, setMaxViewCount] = useState(0); // 0表示无限制
  
  // 提交状态
  const [isCreating, setIsCreating] = useState(false);
  const [createdShare, setCreatedShare] = useState<{
    shareToken: string;
    password: string;
    shareUrl: string;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // 复制状态
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedPassword, setCopiedPassword] = useState(false);

  // 重置表单
  const resetForm = useCallback(() => {
    setPassword(generateRandomPassword());
    setUsePassword(true);
    setExpireHours(168);
    setDataLimit(10);
    setMaxViewCount(0);
    setCreatedShare(null);
    setError(null);
    setCopiedLink(false);
    setCopiedPassword(false);
  }, []);

  // 初始化密码
  useEffect(() => {
    if (isOpen && !password) {
      setPassword(generateRandomPassword());
    }
  }, [isOpen, password]);

  // 关闭对话框
  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  // 生成新密码
  const handleGeneratePassword = useCallback(() => {
    setPassword(generateRandomPassword());
  }, []);

  // 创建分享
  const handleCreateShare = useCallback(async () => {
    setIsCreating(true);
    setError(null);

    try {
      const request: CreateShareRequest = {
        workflowExecutionId,
        dataLimit,
        maxViewCount: maxViewCount > 0 ? maxViewCount : 0,
      };

      // 密码
      if (usePassword) {
        request.password = password;
      }

      // 过期时间
      if (expireHours > 0) {
        const expireAt = new Date();
        expireAt.setHours(expireAt.getHours() + expireHours);
        request.expireAt = expireAt.toISOString();
      }

      const response = await shareApi.createShare(request);
      
      if (response.code === 0) {
        setCreatedShare({
          shareToken: response.data.share.shareToken,
          password: response.data.share.password,
          shareUrl: buildShareUrl(response.data.share.shareToken),
        });
      } else {
        setError(response.message || '创建分享失败');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '创建分享失败');
    } finally {
      setIsCreating(false);
    }
  }, [workflowExecutionId, usePassword, password, expireHours, dataLimit, maxViewCount]);

  // 复制链接
  const handleCopyLink = useCallback(async () => {
    if (!createdShare) return;
    
    try {
      await navigator.clipboard.writeText(createdShare.shareUrl);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // 复制失败
    }
  }, [createdShare]);

  // 复制密码
  const handleCopyPassword = useCallback(async () => {
    if (!createdShare) return;
    
    try {
      await navigator.clipboard.writeText(createdShare.password);
      setCopiedPassword(true);
      setTimeout(() => setCopiedPassword(false), 2000);
    } catch {
      // 复制失败
    }
  }, [createdShare]);

  // 复制完整信息
  const handleCopyFullInfo = useCallback(async () => {
    if (!createdShare) return;
    
    const text = `工作流：${workflowName}
链接：${createdShare.shareUrl}
密码：${usePassword ? createdShare.password : '无密码'}

通过链接可以查看工作流和数据，复制密码后请输入密码访问。`;
    
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(true);
      setTimeout(() => setCopiedLink(false), 2000);
    } catch {
      // 复制失败
    }
  }, [createdShare, workflowName, usePassword]);

  // 对话框标题
  const dialogTitle = (
    <div className="flex items-center gap-2">
      <LinkIcon className="w-5 h-5" />
      {t('share.dialogTitle', '分享工作流结果')}
    </div>
  );

  return (
    <Dialog
      isOpen={isOpen}
      onClose={handleClose}
      title={dialogTitle as unknown as string}
      maxWidth="max-w-lg"
    >
      <div className="mt-2">
        {!createdShare ? (
          // 创建分享表单
          <div className="space-y-6">
            <p className="text-sm text-gray-500">
              {t('share.dialogDescription', '创建一个分享链接，让他人查看你的工作流和数据')}
            </p>

            {/* 密码设置 */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Lock className="w-4 h-4 text-gray-500" />
                  <Label className="font-medium">访问密码</Label>
                </div>
                <Switch 
                  checked={usePassword} 
                  onCheckedChange={setUsePassword}
                />
              </div>
              
              {usePassword && (
                <div className="flex gap-2">
                  <Input
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="6位字母+数字"
                    maxLength={6}
                    className="font-mono tracking-wider"
                  />
                  <Button
                    variant="secondary"
                    onClick={handleGeneratePassword}
                    title="生成新密码"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              )}
              
              <p className="text-xs text-gray-500">
                设置密码后，访问者需要输入密码才能查看数据
              </p>
            </div>

            {/* 有效期设置 */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-gray-500" />
                <Label className="font-medium">有效期</Label>
              </div>
              
              <div className="grid grid-cols-5 gap-2">
                {expireOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setExpireHours(option.value)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      expireHours === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 数据限制设置 */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-gray-500" />
                <Label className="font-medium">可查看数据条数</Label>
              </div>
              
              <div className="grid grid-cols-4 gap-2">
                {dataLimitOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => setDataLimit(option.value)}
                    className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                      dataLimit === option.value
                        ? 'bg-blue-50 border-blue-500 text-blue-700'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 最大查看次数 */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Settings className="w-4 h-4 text-gray-500" />
                <Label className="font-medium">最大查看次数（可选）</Label>
              </div>
              
              <Input
                type="number"
                min={0}
                value={maxViewCount || ''}
                onChange={(e) => setMaxViewCount(parseInt(e.target.value) || 0)}
                placeholder="0表示无限制"
              />
            </div>

            {/* 错误提示 */}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 text-sm rounded-md">
                {error}
              </div>
            )}

            {/* 创建按钮 */}
            <Button
              onClick={handleCreateShare}
              disabled={isCreating || (usePassword && password.length < 4)}
              className="w-full"
            >
              {isCreating ? '创建中...' : '创建分享链接'}
            </Button>
          </div>
        ) : (
          // 分享结果展示
          <div className="space-y-6">
            {/* 成功提示 */}
            <div className="p-4 bg-green-50 text-green-800 rounded-md flex items-center gap-2">
              <Check className="w-5 h-5" />
              <span>分享链接已创建成功！</span>
            </div>

            {/* 分享链接 */}
            <div className="space-y-2">
              <Label className="font-medium">分享链接</Label>
              <div className="flex gap-2">
                <Input
                  value={createdShare.shareUrl}
                  readOnly
                  className="flex-1"
                />
                <Button
                  variant={copiedLink ? 'secondary' : 'primary'}
                  onClick={handleCopyLink}
                >
                  {copiedLink ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>

            {/* 访问密码 */}
            {usePassword && (
              <div className="space-y-2">
                <Label className="font-medium">访问密码</Label>
                <div className="flex gap-2">
                  <Input
                    value={createdShare.password}
                    readOnly
                    className="flex-1 font-mono tracking-wider"
                  />
                  <Button
                    variant={copiedPassword ? 'secondary' : 'primary'}
                    onClick={handleCopyPassword}
                  >
                    {copiedPassword ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            )}

            {/* 配置摘要 */}
            <div className="p-4 bg-gray-50 rounded-md space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-500">工作流：</span>
                <span className="font-medium">{workflowName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">密码保护：</span>
                <span className="font-medium">{usePassword ? '是' : '否'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">有效期：</span>
                <span className="font-medium">
                  {expireOptions.find(o => o.value === expireHours)?.label || '永不过期'}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500">可查看数据：</span>
                <span className="font-medium">
                  {dataLimitOptions.find(o => o.value === dataLimit)?.label}
                </span>
              </div>
            </div>

            {/* 操作按钮 */}
            <div className="flex gap-2">
              <Button
                onClick={handleCopyFullInfo}
                variant="secondary"
                className="flex-1"
              >
                <Copy className="w-4 h-4 mr-2" />
                复制完整信息
              </Button>
              <Button
                onClick={resetForm}
                variant="secondary"
              >
                创建新的
              </Button>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
}
